import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaMap } from "react-icons/fa";
import ToggleOverlay from "./ToggleOverlay";
import "../styles/BasemapToggle.css";

const BasemapToggle = ({
  basemapsConfig,
  currentBasemap,
  changeBasemap,
  map,
  labelsMap,
  boundariesMap,
  roadsMap,
  layersMapRef,
  isOpen,
  isExpanded,
  onClose,
  onFeatureSelect,
  selectedFeature,
  featureType,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(!isExpanded);
  const [overlaysVisible, setOverlaysVisible] = useState({
    boundaries: true,
    labels: true,
    roads: true,
    divisions: false,
    districts: false,
  });

  useEffect(() => {
    setIsCollapsed(!isExpanded);
  }, [isExpanded]);

  // Disable double-click zoom on layersMapRef
  useEffect(() => {
    if (layersMapRef?.current) {
      layersMapRef.current.doubleClickZoom.disable();
    }
  }, [layersMapRef]);

  const toggleOverlayContainer = (overlayKey, overlayMap) => {
    if (!overlayMap) {
      console.warn(`Cannot toggle overlay for "${overlayKey}". No map ref found.`);
      return;
    }

    const container = overlayMap.getContainer();
    if (!container) {
      console.warn(`Cannot locate DOM container for overlay "${overlayKey}".`);
      return;
    }

    setOverlaysVisible((prev) => {
      const newVisibility = !prev[overlayKey];
      container.style.display = newVisibility ? "block" : "none";
      return { ...prev, [overlayKey]: newVisibility };
    });
  };

  const toggleBoundaries = () => toggleOverlayContainer("boundaries", boundariesMap);
  const toggleLabels = () => toggleOverlayContainer("labels", labelsMap);
  const toggleStreets = () => toggleOverlayContainer("roads", roadsMap);

  const loadDistrictsLayer = () => {
    const mapObj = layersMapRef?.current;
    if (!mapObj) return;

    window.selectedDistrictName = window.selectedDistrictName || null;

    if (!mapObj.getSource("districts-source")) {
      mapObj.addSource("districts-source", {
        type: "vector",
        url: "mapbox://ibau1.9oui2gic",
      });
    }

    if (!mapObj.getLayer("districts-layer")) {
      try {
        mapObj.addLayer({
          id: "districts-fill-layer",
          type: "fill",
          source: "districts-source",
          "source-layer": "Sarawak_Districts-1480hj",
          layout: { visibility: "visible" },
          paint: { "fill-color": "#000000", "fill-opacity": 0 },
        });

        mapObj.addLayer({
          id: "districts-layer",
          type: "line",
          source: "districts-source",
          "source-layer": "Sarawak_Districts-1480hj",
          layout: {
            visibility: "visible",
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#000000",
            "line-width": 0.5,
            "line-opacity": 1,
          },
        });

        mapObj.addLayer({
          id: "districts-highlight-layer",
          type: "line",
          source: "districts-source",
          "source-layer": "Sarawak_Districts-1480hj",
          layout: {
            visibility: "visible",
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#00FF00",
            "line-width": 0.5,
            "line-opacity": 1,
          },
          filter: ["==", ["get", "name"], ""],
        });

        mapObj.moveLayer("districts-fill-layer");
        mapObj.moveLayer("districts-layer");
        mapObj.moveLayer("districts-highlight-layer");
      } catch (err) {
        console.error("Error adding district layers:", err);
      }
    } else {
      mapObj.setLayoutProperty("districts-layer", "visibility", "visible");
      mapObj.setLayoutProperty("districts-fill-layer", "visibility", "visible");
      if (mapObj.getLayer("districts-highlight-layer")) {
        mapObj.setLayoutProperty("districts-highlight-layer", "visibility", "visible");
        mapObj.moveLayer("districts-highlight-layer");
      }
      if (window.selectedDistrictName) {
        mapObj.setFilter("districts-highlight-layer", ["==", ["get", "name"], window.selectedDistrictName]);
      } else {
        mapObj.setFilter("districts-highlight-layer", ["==", ["get", "name"], ""]);
      }
    }
  };

  const toggleDistricts = () => {
    setOverlaysVisible((prev) => {
      const newVal = !prev.districts;
      const mapObj = layersMapRef?.current;
      if (mapObj) {
        if (newVal) {
          loadDistrictsLayer();
          if (mapObj.getLayer("districts-layer")) {
            mapObj.moveLayer("districts-fill-layer");
            mapObj.moveLayer("districts-layer");
          }
          if (mapObj.getLayer("districts-highlight-layer")) {
            mapObj.moveLayer("districts-highlight-layer");
          }
        } else {
          window.selectedDistrictName = null;
          if (mapObj.getLayer("districts-highlight-layer")) {
            mapObj.setFilter("districts-highlight-layer", ["==", ["get", "name"], ""]);
          }
          if (mapObj.getLayer("districts-layer")) {
            mapObj.setLayoutProperty("districts-layer", "visibility", "none");
            mapObj.setLayoutProperty("districts-fill-layer", "visibility", "none");
          }
          if (mapObj.getLayer("districts-highlight-layer")) {
            mapObj.setLayoutProperty("districts-highlight-layer", "visibility", "none");
          }
        }
      }
      return { ...prev, districts: newVal };
    });
  };

  const loadDivisionsLayer = () => {
    const mapObj = layersMapRef?.current;
    if (!mapObj) return;

    window.selectedDivisionName = window.selectedDivisionName || null;

    if (!mapObj.getSource("divisions-source")) {
      mapObj.addSource("divisions-source", {
        type: "vector",
        url: "mapbox://ibau1.3o069mml",
        minzoom: 0,
        maxzoom: 22,
      });
    }

    if (!mapObj.getLayer("divisions-layer")) {
      try {
        mapObj.addLayer({
          id: "divisions-fill-layer",
          type: "fill",
          source: "divisions-source",
          "source-layer": "Sarawak_Division-47gbet",
          minzoom: 0,
          maxzoom: 22,
          layout: { visibility: "visible" },
          paint: { "fill-color": "#000000", "fill-opacity": 0 },
        });

        mapObj.addLayer({
          id: "divisions-layer",
          type: "line",
          source: "divisions-source",
          "source-layer": "Sarawak_Division-47gbet",
          minzoom: 0,
          maxzoom: 22,
          layout: {
            visibility: "visible",
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#000000",
            "line-width": 0.5,
            "line-opacity": 1,
          },
        });

        mapObj.addLayer({
          id: "divisions-highlight-layer",
          type: "line",
          source: "divisions-source",
          "source-layer": "Sarawak_Division-47gbet",
          minzoom: 0,
          maxzoom: 22,
          layout: {
            visibility: "visible",
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#0dd2ef",
            "line-width": 2,
            "line-opacity": 1,
          },
          filter: ["==", ["get", "Division"], ""],
        });

        mapObj.moveLayer("divisions-fill-layer");
        mapObj.moveLayer("divisions-layer");
        mapObj.moveLayer("divisions-highlight-layer");
      } catch (err) {
        console.error("Error adding division layers:", err);
      }
    } else {
      mapObj.setLayoutProperty("divisions-layer", "visibility", "visible");
      mapObj.setLayoutProperty("divisions-fill-layer", "visibility", "visible");
      if (mapObj.getLayer("divisions-highlight-layer")) {
        mapObj.setLayoutProperty("divisions-highlight-layer", "visibility", "visible");
        mapObj.moveLayer("divisions-highlight-layer");
      }
      if (window.selectedDivisionName) {
        mapObj.setFilter("divisions-highlight-layer", ["==", ["get", "Division"], window.selectedDivisionName]);
      } else {
        mapObj.setFilter("divisions-highlight-layer", ["==", ["get", "Division"], ""]);
      }
    }
  };

  const toggleDivisions = () => {
    setOverlaysVisible((prev) => {
      const newVal = !prev.divisions;
      const mapObj = layersMapRef?.current;
      if (mapObj) {
        if (newVal) {
          loadDivisionsLayer();
          if (mapObj.getLayer("divisions-layer")) {
            mapObj.moveLayer("divisions-fill-layer");
            mapObj.moveLayer("divisions-layer");
          }
          if (mapObj.getLayer("divisions-highlight-layer")) {
            mapObj.moveLayer("divisions-highlight-layer");
          }
        } else {
          window.selectedDivisionName = null;
          if (mapObj.getLayer("divisions-highlight-layer")) {
            mapObj.setFilter("divisions-highlight-layer", ["==", ["get", "Division"], ""]);
          }
          if (mapObj.getLayer("divisions-layer")) {
            mapObj.setLayoutProperty("divisions-layer", "visibility", "none");
            mapObj.setLayoutProperty("divisions-fill-layer", "visibility", "none");
          }
          if (mapObj.getLayer("divisions-highlight-layer")) {
            mapObj.setLayoutProperty("divisions-highlight-layer", "visibility", "none");
          }
        }
      }
      return { ...prev, divisions: newVal };
    });
  };

  const BasemapContent = () => {
    const satelliteBasemaps = basemapsConfig.filter((b) =>
      ["satellite-streets", "light", "streets", "outdoors", "dark"].includes(b.id)
    );
    const stylizedOptions = [
      { id: "light", name: "Light" },
      { id: "streets", name: "Streets" },
      { id: "outdoors", name: "Topography" },
      { id: "dark", name: "Dark" },
    ];

    const [currentStyle, setCurrentStyle] = useState(currentBasemap.id || "light");

    const handleBasemapChange = (basemapId) => {
      const selected = basemapsConfig.find((b) => b.id === basemapId);
      if (selected) {
        changeBasemap(basemapId);
        setCurrentStyle(basemapId);
      }
    };

    const handleStyleChange = (styleId) => {
      const selected = basemapsConfig.find((b) => b.id === styleId);
      if (selected) {
        changeBasemap(styleId);
        setCurrentStyle(styleId);
      }
    };

    return (
      <>
        <h3 className="section-title">Base Layer</h3>
        <h3 className="default-label">Satellite Imagery</h3>
        <div className="basemap-options-list">
          {satelliteBasemaps
            .filter((b) => b.id === "satellite-streets")
            .map((basemap) => (
              <label key={basemap.id} className="basemap-option">
                <input
                  type="radio"
                  name="basemap"
                  checked={currentBasemap.id === basemap.id}
                  onChange={() => handleBasemapChange(basemap.id)}
                />
                <span className="basemap-label">{basemap.name}</span>
                {basemap.id === "satellite-streets" && (
                  <span className="default-label">(default)</span>
                )}
              </label>
            ))}
        </div>

        <h3 className="default-label">Stylized</h3>
        <div className="basemap-options-list">
          {stylizedOptions.map((style) => (
            <label key={style.id} className="basemap-option">
              <input
                type="radio"
                name="style"
                checked={currentStyle === style.id}
                onChange={() => handleStyleChange(style.id)}
              />
              <span className="basemap-label">{style.name}</span>
            </label>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="basemap-toggle-container">
      <ToggleOverlay
        title="Base Map"
        icon={FaMap}
        isCollapsed={isCollapsed}
        onCollapse={() => setIsCollapsed(!isCollapsed)}
        onClose={onClose}
        className="basemap-dropdown"
        width={400}
        style={{ display: isOpen ? 'block' : 'none' }} // Control visibility with CSS
      >
        <BasemapContent />

        <div className="elements-section">
          <h3 className="elements-title">Elements</h3>
          <div className="basemap-toggle-features">
            <button
              onClick={toggleBoundaries}
              className={`feature-button ${
                overlaysVisible.boundaries ? "active" : ""
              }`}
            >
              Boundaries
            </button>
            <button
              onClick={toggleLabels}
              className={`feature-button ${
                overlaysVisible.labels ? "active" : ""
              }`}
            >
              Labels
            </button>
            <button
              onClick={toggleStreets}
              className={`feature-button ${
                overlaysVisible.roads ? "active" : ""
              }`}
            >
              Roads
            </button>
            <button
              onClick={toggleDivisions}
              className={`feature-button ${
                overlaysVisible.divisions ? "active" : ""
              }`}
            >
              Divisions
            </button>
            <button
              onClick={toggleDistricts}
              className={`feature-button ${
                overlaysVisible.districts ? "active" : ""
              }`}
            >
              Districts
            </button>
          </div>
        </div>
      </ToggleOverlay>
    </div>
  );
};

BasemapToggle.propTypes = {
  basemapsConfig: PropTypes.array.isRequired,
  currentBasemap: PropTypes.object.isRequired,
  changeBasemap: PropTypes.func.isRequired,
  map: PropTypes.object,
  labelsMap: PropTypes.object,
  boundariesMap: PropTypes.object,
  roadsMap: PropTypes.object,
  layersMapRef: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFeatureSelect: PropTypes.func,
  selectedFeature: PropTypes.object,
  featureType: PropTypes.string,
};

export default BasemapToggle;
